import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  getInitialLtType,
  getInitialLosType,
  getRangeType,
} from "../utils/targetingConditionsInitial";

/**
 * Sub-hook of `useTargetingConditionsState` owning Lead Time (LT) and
 * Length Of Stay (LOS) state, handlers, and validation effects.
 *
 * Exposes the same field names the parent orchestrator used to expose
 * directly so existing consumers continue to work unchanged.
 */
export function useTcStayLeadConditions({
  promo,
  tc,
  setTc,
  handleFieldChange,
  handleInternalFieldChange,
  isInternalUpdateRef,
}) {
  // Lead time type
  const [ltType, setLTType] = useState(() => getInitialLtType({ promo }));

  // Length of stay type
  const [losType, setLOSType] = useState(() => getInitialLosType({ promo }));

  // Validation error states
  const [hasLeadTimeError, setHasLeadTimeError] = useState(false);
  const [hasLosError, setHasLosError] = useState(false);

  // Derived values
  const minBookingLeadTime =
    promo?.rateRule?.ruleVO?.bookingVO?.minBookingLeadTime;
  const maxBookingLeadTime =
    promo?.rateRule?.ruleVO?.bookingVO?.maxBookingLeadTime;
  const minLos = promo?.rateRule?.ruleVO?.stayVO?.minLengthOfStay;
  const maxLos = promo?.rateRule?.ruleVO?.stayVO?.maxLengthOfStay;

  // Sync LT/LOS type when promo changes externally. Internal updates
  // (driven by user input on the lead-time / length-of-stay fields)
  // are skipped so the user-selected radio type is not clobbered when
  // they edit min/max values. The shared `isInternalUpdateRef` is owned
  // by the parent orchestrator's combined sync effect; reading it here
  // mirrors that gate without mutating it.
  useEffect(() => {
    if (isInternalUpdateRef?.current) return;
    const bookingVO = promo?.rateRule?.ruleVO?.bookingVO;
    const stayVO = promo?.rateRule?.ruleVO?.stayVO;
    setLTType(
      getRangeType(bookingVO?.minBookingLeadTime, bookingVO?.maxBookingLeadTime)
    );
    setLOSType(getRangeType(stayVO?.minLengthOfStay, stayVO?.maxLengthOfStay));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promo]);

  // Lead time handlers
  const setShowLT = useCallback(() => {
    if (tc.showLT) {
      handleFieldChange("rateRule.ruleVO.bookingVO.minBookingLeadTime", null);
      handleFieldChange("rateRule.ruleVO.bookingVO.maxBookingLeadTime", null);
      setTc({ ...tc, showLT: false });
    } else {
      if (minBookingLeadTime != null) {
        handleFieldChange(
          "rateRule.ruleVO.bookingVO.minBookingLeadTime",
          minBookingLeadTime
        );
      }
      if (maxBookingLeadTime != null) {
        handleFieldChange(
          "rateRule.ruleVO.bookingVO.maxBookingLeadTime",
          maxBookingLeadTime
        );
      }
      setTc({ ...tc, showLT: true });
    }
  }, [tc, setTc, handleFieldChange, minBookingLeadTime, maxBookingLeadTime]);

  const setLTMinMax = useCallback(
    (value, type) => {
      if (value && value < 1) {
        toast.error("Minimum value is 1");
        return;
      }
      if (value && value > 999) {
        toast.error("Maximum value is 999");
        return;
      }
      if (type === "1") {
        handleInternalFieldChange(
          "rateRule.ruleVO.bookingVO.minBookingLeadTime",
          value
        );
        handleInternalFieldChange(
          "rateRule.ruleVO.bookingVO.maxBookingLeadTime",
          null
        );
      } else if (type === "2") {
        handleInternalFieldChange(
          "rateRule.ruleVO.bookingVO.minBookingLeadTime",
          null
        );
        handleInternalFieldChange(
          "rateRule.ruleVO.bookingVO.maxBookingLeadTime",
          value
        );
      }
    },
    [handleInternalFieldChange]
  );

  const setLTRange = useCallback(
    (value, type) => {
      if (value && value < 1) {
        toast.error("Minimum value is 1");
        return;
      }
      if (value && value > 999) {
        toast.error("Maximum value is 999");
        return;
      }
      if (type === "min") {
        handleInternalFieldChange(
          "rateRule.ruleVO.bookingVO.minBookingLeadTime",
          value
        );
      } else {
        handleInternalFieldChange(
          "rateRule.ruleVO.bookingVO.maxBookingLeadTime",
          value
        );
      }
    },
    [handleInternalFieldChange]
  );

  // Length of stay handlers
  const setShowLOS = useCallback(() => {
    if (tc.showLOS) {
      handleFieldChange("rateRule.ruleVO.stayVO.minLengthOfStay", null);
      handleFieldChange("rateRule.ruleVO.stayVO.maxLengthOfStay", null);
      setTc({ ...tc, showLOS: false });
    } else {
      if (minLos != null) {
        handleFieldChange("rateRule.ruleVO.stayVO.minLengthOfStay", minLos);
      }
      if (maxLos != null) {
        handleFieldChange("rateRule.ruleVO.stayVO.maxLengthOfStay", maxLos);
      }
      setTc({ ...tc, showLOS: true });
    }
  }, [tc, setTc, handleFieldChange, minLos, maxLos]);

  const setLosMinMax = useCallback(
    (value, type) => {
      if (value && value < 1) {
        toast.error("Minimum value is 1");
        return;
      }
      if (value && value > 28) {
        toast.error("Maximum value is 28");
        return;
      }
      if (type === "1") {
        handleInternalFieldChange(
          "rateRule.ruleVO.stayVO.minLengthOfStay",
          value
        );
        handleInternalFieldChange(
          "rateRule.ruleVO.stayVO.maxLengthOfStay",
          null
        );
      } else if (type === "2") {
        handleInternalFieldChange(
          "rateRule.ruleVO.stayVO.minLengthOfStay",
          null
        );
        handleInternalFieldChange(
          "rateRule.ruleVO.stayVO.maxLengthOfStay",
          value
        );
      }
    },
    [handleInternalFieldChange]
  );

  const setLosRange = useCallback(
    (value, type) => {
      if (value && value < 1) {
        toast.error("Minimum value is 1");
        return;
      }
      if (value && value > 28) {
        toast.error("Maximum value is 28");
        return;
      }
      if (type === "min") {
        handleInternalFieldChange(
          "rateRule.ruleVO.stayVO.minLengthOfStay",
          value
        );
      } else {
        handleInternalFieldChange(
          "rateRule.ruleVO.stayVO.maxLengthOfStay",
          value
        );
      }
    },
    [handleInternalFieldChange]
  );

  // Effect to update LOS error state in real-time
  useEffect(() => {
    const minLosNum = Number(minLos);
    const maxLosNum = Number(maxLos);
    const hasError = minLos && maxLos && minLosNum > maxLosNum;
    setHasLosError(!!hasError);
  }, [minLos, maxLos, tc.showLOS]);

  // Effect to update Lead Time error state in real-time
  useEffect(() => {
    const minLeadTimeNum = Number(minBookingLeadTime);
    const maxLeadTimeNum = Number(maxBookingLeadTime);
    const hasError =
      minBookingLeadTime &&
      maxBookingLeadTime &&
      minLeadTimeNum > maxLeadTimeNum;
    setHasLeadTimeError(!!hasError);
  }, [minBookingLeadTime, maxBookingLeadTime, tc.showLT]);

  return {
    // Lead time
    ltType,
    setLTType,
    minBookingLeadTime,
    maxBookingLeadTime,
    setShowLT,
    setLTMinMax,
    setLTRange,

    // Length of stay
    losType,
    setLOSType,
    minLos,
    maxLos,
    setShowLOS,
    setLosMinMax,
    setLosRange,

    // Validation errors
    hasLeadTimeError,
    hasLosError,
  };
}
